/**
 * Activity & Audit Log Page
 *
 * Enterprise features for billion-scale:
 * - Cursor-based pagination (scales to billions of events)
 * - Real-time event streaming ready
 * - Debounced search with request cancellation
 * - Infinite scroll with intersection observer
 * - Optimistic UI for better perceived performance
 * - Request deduplication
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { activityApi, analyticsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useDebouncedValue, useIntersectionObserver } from '../../hooks/useDataFetch';
import { requestManager } from '../../lib/RequestManager';
import './ActivityLog.css';

// Constants
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const STATS_REFRESH_INTERVAL = 30000; // Refresh stats every 30 seconds

const ActivityLog = () => {
  const { showToast } = useToast();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [filterType, setFilterType] = useState('');
  const [cursor, setCursor] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  // Debounced search
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Build query params
  const queryParams = useMemo(() => ({
    limit: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(filterTenant && { tenant: filterTenant }),
    ...(filterType && { type: filterType }),
    ...(cursor && { cursor }),
  }), [debouncedSearch, filterTenant, filterType, cursor]);

  // Cache key for request deduplication
  const cacheKey = useMemo(() =>
    `activity:${debouncedSearch}:${filterTenant}:${filterType}:${cursor}`,
    [debouncedSearch, filterTenant, filterType, cursor]
  );

  // Fetch events
  const {
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    () => activityApi.list(queryParams),
    {
      key: cacheKey,
      cacheTTL: 15000, // Short cache for activity logs
      enabled: true,
      onSuccess: (response) => {
        const newEvents = response.events || response.data || response.logs || [];
        const nextCursor = response.nextCursor || response.pagination?.nextCursor;

        if (cursor) {
          // Append for pagination
          setAllEvents(prev => [...prev, ...newEvents]);
        } else {
          // Replace for new search/filter
          setAllEvents(newEvents);
        }

        setHasMore(!!nextCursor && newEvents.length === PAGE_SIZE);
      },
      onError: (err) => {
        showToast(err.message || 'Failed to load activity logs', 'error');
      },
    }
  );

  // Fetch real-time stats with polling
  const { data: statsData } = useQuery(
    () => analyticsApi.realtime(),
    {
      key: 'activity:stats',
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
      onError: () => {
        // Silently fail for stats - not critical
      },
    }
  );

  // Compute stats from response or use defaults
  const stats = useMemo(() => {
    const data = statsData || {};
    return [
      {
        label: 'Events Today',
        value: data.eventsToday?.toLocaleString() || '—',
        change: data.eventsTrend ? `${data.eventsTrend}%` : undefined,
        changeType: data.eventsTrend > 0 ? 'positive' : 'neutral',
      },
      {
        label: 'Auth Events',
        value: data.authEvents?.toLocaleString() || '—',
        change: data.authPercent ? `${data.authPercent}% of total` : undefined,
        changeType: 'positive',
      },
      {
        label: 'Admin Actions',
        value: data.adminActions?.toLocaleString() || '—',
        change: data.flaggedActions ? `${data.flaggedActions} flagged` : undefined,
        changeType: data.flaggedActions > 0 ? 'warning' : 'positive',
      },
      {
        label: 'API Calls',
        value: data.apiCalls?.toLocaleString() || '—',
        change: 'Per minute avg',
        changeType: 'positive',
      },
    ];
  }, [statsData]);

  // Reset when filters change
  useEffect(() => {
    setCursor(null);
    setAllEvents([]);
    setHasMore(true);
    requestManager.invalidateCache(`activity:${debouncedSearch}:${filterTenant}:${filterType}`);
  }, [debouncedSearch, filterTenant, filterType]);

  // Infinite scroll observer
  const [loadMoreRef, isNearBottom] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '200px',
  });

  // Load more when scrolling
  useEffect(() => {
    if (isNearBottom && hasMore && !isLoading && !isFetching && allEvents.length > 0) {
      const lastEvent = allEvents[allEvents.length - 1];
      if (lastEvent?.id) {
        setCursor(lastEvent.id);
      }
    }
  }, [isNearBottom, hasMore, isLoading, isFetching, allEvents]);

  // Handlers
  const handleRefresh = useCallback(() => {
    setCursor(null);
    setAllEvents([]);
    requestManager.invalidateCache('activity');
    refetch();
  }, [refetch]);

  const handleExport = useCallback(async () => {
    try {
      showToast('Preparing export...', 'info');
      const result = await activityApi.export({
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filterTenant && { tenant: filterTenant }),
        ...(filterType && { type: filterType }),
        format: 'csv',
      });

      // Create download link
      const blob = new Blob([result], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      showToast('Export downloaded', 'success');
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  }, [debouncedSearch, filterTenant, filterType, showToast]);

  // Helpers
  const getTypeClass = (type) => {
    const classes = {
      auth: 'event-auth',
      login: 'event-auth',
      logout: 'event-auth',
      admin: 'event-admin',
      security: 'event-security',
      fraud: 'event-security',
      api: 'event-api',
      billing: 'event-billing',
      payment: 'event-billing',
      user: 'event-user',
      create: 'event-user',
      update: 'event-admin',
      delete: 'event-security',
    };
    return classes[type] || 'event-default';
  };

  const getEventIcon = (type) => {
    const icons = {
      auth: 'lock',
      login: 'log-in',
      logout: 'log-out',
      admin: 'settings',
      security: 'shield',
      fraud: 'alert-triangle',
      api: 'zap',
      billing: 'credit-card',
      payment: 'dollar-sign',
      user: 'user',
      create: 'plus',
      update: 'edit',
      delete: 'trash',
    };
    return icons[type] || 'activity';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '—';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="activity-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Activity & Audit Log</h1>
          <p className="page-subtitle">
            Monitor all system events and user actions
            {allEvents.length > 0 && (
              <span className="events-count"> ({allEvents.length}{hasMore ? '+' : ''} loaded)</span>
            )}
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="download"
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
          >
            {isFetching ? 'Syncing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="activity-error">
          <Icon name="alert-triangle" size="sm" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Icon name="search" size="sm" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search events"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <Icon name="x" size="xs" />
            </button>
          )}
        </div>

        <select
          className="filter-select"
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value)}
          aria-label="Filter by tenant"
        >
          <option value="">All Tenants</option>
          <option value="Cachee">Cachee</option>
          <option value="L100.ai">L100.ai</option>
          <option value="SuperSearch.ai">SuperSearch.ai</option>
          <option value="Mirror1.ai">Mirror1.ai</option>
          <option value="BabyKong.ai">BabyKong.ai</option>
        </select>

        <select
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          aria-label="Filter by event type"
        >
          <option value="">All Event Types</option>
          <option value="auth">Authentication</option>
          <option value="admin">Admin Actions</option>
          <option value="api">API Calls</option>
          <option value="security">Security Events</option>
          <option value="billing">Billing</option>
          <option value="user">User Management</option>
        </select>
      </div>

      {/* Events Timeline */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Event Timeline</h3>
          {allEvents.length > 0 && (
            <span className="section-meta">{allEvents.length} events loaded</span>
          )}
        </div>

        <div className="events-timeline">
          {/* Loading skeleton */}
          {isLoading && allEvents.length === 0 && (
            [...Array(10)].map((_, i) => (
              <div key={`skeleton-${i}`} className="event-item skeleton-event">
                <div className="event-icon skeleton-icon"></div>
                <div className="event-content">
                  <div className="skeleton-line" style={{ width: '60%' }}></div>
                  <div className="skeleton-line short" style={{ width: '40%' }}></div>
                </div>
                <div className="skeleton-time"></div>
              </div>
            ))
          )}

          {/* Event rows */}
          {allEvents.map((event) => (
            <div
              key={event.id}
              className={`event-item ${getTypeClass(event.type || event.action)}`}
              data-event-id={event.id}
            >
              <div className="event-icon">
                <Icon name={event.icon || getEventIcon(event.type || event.action)} size="sm" />
              </div>
              <div className="event-content">
                <div className="event-title">
                  {event.title || event.message || event.action}
                </div>
                <div className="event-meta">
                  <span className="event-user">{event.user || event.userEmail || 'System'}</span>
                  <span className="dot">•</span>
                  <span className="event-tenant">{event.tenant || event.tenantName || '—'}</span>
                  <span className="dot">•</span>
                  <span className="event-ip">{event.ip || event.ipAddress || '—'}</span>
                </div>
              </div>
              <div className="event-time" title={formatTime(event.timestamp || event.createdAt)}>
                {formatRelativeTime(event.timestamp || event.createdAt)}
              </div>
              <Button
                variant="ghost"
                size="xs"
                icon="chevron-right"
                iconOnly
                aria-label="View event details"
              />
            </div>
          ))}

          {/* Empty state */}
          {!isLoading && allEvents.length === 0 && (
            <div className="events-empty">
              <Icon name="activity" size="lg" />
              <h3>No events found</h3>
              <p>
                {searchQuery || filterTenant || filterType
                  ? 'Try adjusting your search or filters'
                  : 'Activity events will appear here as they occur'}
              </p>
            </div>
          )}
        </div>

        {/* Load more trigger for infinite scroll */}
        {hasMore && allEvents.length > 0 && (
          <div ref={loadMoreRef} className="load-more-trigger">
            {isFetching && (
              <div className="loading-more">
                <div className="spinner"></div>
                <span>Loading more events...</span>
              </div>
            )}
          </div>
        )}

        {/* End of list */}
        {!hasMore && allEvents.length > 0 && (
          <div className="end-of-list">
            All {allEvents.length} events loaded
          </div>
        )}
      </section>
    </div>
  );
};

export default ActivityLog;
